import { Component } from 'react';

import { Trans } from '@lingui/react/macro';

import { reportError, currentRouteTag } from '../../lib/errorReporter';
import { isChunkLoadError } from '../../utils/lazyWithReload';
import { msg } from '@lingui/core/macro';
import { i18n } from '../../i18n/runtime.js';

/**
 * ErrorBoundary tái dùng — chặn crash của subtree, hiện fallback thân thiện
 * thay vì trắng trang (vd report page crash do data thiếu field).
 *
 * ⚠️ HAI VIỆC BẮT BUỘC Ở ĐÂY, ĐỪNG GỠ (sự cố 18/08/2026):
 *
 * 1. BẮN LỖI VỀ SENTRY. Trước đây chỉ `console.error` → console nằm ở máy KHÁCH, mình
 *    không đọc được. Báo cáo CRM lỗi cả buổi mà GlitchTip (hub.cnvwork.com/monitor)
 *    trống trơn nên không ai biết; phải SSH + đọc DB + hỏi sếp Phú mới lần ra.
 *    Gửi qua kernel `lib/errorReporter` (host cắm Sentry vào — Core chưa có thì no-op).
 *
 * 2. PHÂN BIỆT LỖI TẢI CHUNK. Deploy FE rsync --delete xoá chunk-hash cũ; tab nào còn giữ
 *    index.html cũ thì import() đòi file đã mất → 404. Đây KHÔNG phải lỗi dữ liệu, và nút
 *    "Thử lại" (reset state → render lại → import lại đúng file đã mất) KHÔNG cứu được.
 *    Chỉ `location.reload()` mới lấy index.html mới. Thông điệp cũ ("có thể do dữ liệu
 *    chưa sẵn sàng") đổ oan cho dữ liệu và đẩy người đi soi BE trong khi BE trả 200.
 *
 * Props:
 *   - children
 *   - fallback?: (error, reset) => ReactNode   // tùy biến fallback (KHÔNG áp cho lỗi chunk —
 *                                              // lỗi chunk luôn dùng UI "tải lại trang")
 *   - title?: string                            // tiêu đề mặc định
 *   - message?: string                          // mô tả mặc định
 *   - name?: string                             // tên vùng bọc, dùng làm tag Sentry `boundary`
 *   - silent?: boolean                          // hỏng thì render null, KHÔNG vẽ gì lên màn hình
 *                                              // (dành cho tiện ích nền — xem BackgroundWidget)
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, chunkStale: false };
  }

  static getDerivedStateFromError(error) {
    // Tính chunkStale NGAY ở đây (không đợi componentDidCatch) — React gọi hàm này trước
    // lần render fallback đầu tiên, nên UI không bao giờ loé thông điệp sai rồi mới đổi.
    return { hasError: true, error, chunkStale: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error?.message, info?.componentStack);
    reportError(error, {
      tags: {
        boundary: this.props.name || componentNameOf(info) || 'unknown',
        route: currentRouteTag(),
        // 'true' = lỗi tải chunk lọt tới ĐÂY, tức lazyWithReload đã hết lượt reload mà chunk
        // vẫn 404 ⇒ KHÔNG còn là transient tự khỏi. Host lọc theo tag này (xem sentry.js).
        chunk_load: isChunkLoadError(error) ? 'true' : 'false',
      },
      extra: {
        componentStack: info?.componentStack,
        // Đường dẫn thô (KHÔNG kèm query string — query có thể chứa token).
        pathname: safePathname(),
      },
    });
  }

  reset = () => this.setState({ hasError: false, error: null, chunkStale: false });

  reload = () => {
    try { window.location.reload(); } catch { /* iframe bị chặn — không làm gì thêm */ }
  };

  render() {
    if (this.state.hasError) {
      // `silent` — vùng bọc là TIỆN ÍCH NỀN (softphone, đăng ký web push, bảng lệnh Cmd+K,
      // phím tắt chụp màn hình…): mount toàn cục, không có mảng nào trên màn hình là "của
      // nó". Hỏng thì tắt đúng nó, KHÔNG chèn panel lỗi vào giữa trang người ta đang đọc —
      // kể cả panel "tải lại trang" của nhánh chunk bên dưới, vì widget nền không phải thứ
      // người dùng đang chờ xem.
      //
      // Đây KHÔNG phải nuốt lỗi im lặng (xem feedback_no_silent_fail_fe): componentDidCatch
      // ở trên vẫn console.error ĐẦY ĐỦ + bắn về Sentry kèm tag `boundary`/`route`. Im ở
      // chỗ NGƯỜI DÙNG nhìn, ồn ở chỗ NGƯỜI TRỰC nhìn — đúng thứ tự.
      if (this.props.silent) return null;

      // Lỗi chunk đi trước cả `fallback` của caller: fallback tùy biến được viết cho lỗi
      // DỮ LIỆU, đưa lỗi chunk vào đó là lặp lại đúng vụ chẩn đoán nhầm 18/08.
      if (this.state.chunkStale) {
        return (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="text-base font-semibold text-gray-800 mb-1">
              <Trans>Hệ thống vừa cập nhật bản mới</Trans>
            </div>
            <div className="text-sm text-gray-500 mb-4 max-w-md">
              <Trans>Trang bạn đang mở thuộc bản cũ nên không tải được phần này. Hãy tải lại trang để dùng bản mới nhất — dữ liệu của bạn không bị ảnh hưởng.</Trans>
            </div>
            <button
              onClick={this.reload}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
            >
              <Trans>Tải lại trang</Trans>
            </button>
          </div>
        );
      }
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="text-base font-semibold text-gray-800 mb-1">
            {this.props.title || i18n._(msg`Không tải được nội dung`)}
          </div>
          <div className="text-sm text-gray-500 mb-4 max-w-md">
            {this.props.message || i18n._(msg`Đã có lỗi khi hiển thị phần này. Vui lòng thử lại hoặc liên hệ hỗ trợ nếu lỗi tiếp diễn.`)}
          </div>
          <button
            onClick={this.reset}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
          >
            <Trans>Thử lại</Trans>
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Tên component ném lỗi, lấy từ dòng đầu componentStack ("    at ClvReport (http://…)").
 * Bản prod minify tên nên có thể ra 1-2 ký tự — vẫn hơn 'unknown', và tag `route` mới là
 * thứ định vị chính.
 */
function componentNameOf(info) {
  const line = String(info?.componentStack || '').split('\n').map((s) => s.trim()).find(Boolean);
  const m = line && line.match(/^(?:at\s+)?([A-Za-z0-9_$.]+)/);
  return m ? m[1] : null;
}

function safePathname() {
  try { return window.location.pathname; } catch { return null; }
}
